import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import {
  createTaskDropdown,
  updateTaskDropdown,
  type TaskDropdownRow,
} from '../../services/taskDropdownService';
import { slugify } from '../../utils/slugify';

interface Props {
  open: boolean;
  /** Provided → edit mode; omitted/null → create mode. */
  dropdown?: TaskDropdownRow | null;
  onClose: () => void;
  onSaved: (saved: TaskDropdownRow) => void;
}

/**
 * Reusable create/edit modal for a task dropdown (reusable option set).
 * Persists via the per-row task-dropdowns API. Used by the Task Dropdowns
 * admin table and inline from the task editor's dropdown picker.
 */
export const TaskDropdownFormDialog: React.FC<Props> = ({
  open,
  dropdown,
  onClose,
  onSaved,
}) => {
  const isEdit = !!dropdown;
  const [displayName, setDisplayName] = useState('');
  const [dropdownId, setDropdownId] = useState('');
  const [description, setDescription] = useState('');
  const [optionsText, setOptionsText] = useState('');
  const [idManuallyEdited, setIdManuallyEdited] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-seed when opening for a different row.
  useEffect(() => {
    if (!open) return;
    setDisplayName(dropdown?.displayName ?? '');
    setDropdownId(dropdown?.dropdownId ?? '');
    setDescription(dropdown?.description ?? '');
    setOptionsText((dropdown?.options ?? []).join('\n'));
    setIdManuallyEdited(!!dropdown);
    setError(null);
  }, [open, dropdown]);

  const handleNameChange = (value: string) => {
    setDisplayName(value);
    if (!idManuallyEdited && !isEdit) {
      setDropdownId(slugify(value));
    }
  };

  const handleSubmit = async () => {
    const name = displayName.trim();
    const slug = dropdownId.trim();
    const options = optionsText
      .split('\n')
      .map((o) => o.trim())
      .filter(Boolean);

    if (!name) {
      setError('Display name is required');
      return;
    }
    if (!isEdit && !slug) {
      setError('Dropdown ID is required');
      return;
    }
    if (options.length === 0) {
      setError('Add at least one option');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const saved = isEdit
        ? await updateTaskDropdown(dropdown!.id ?? dropdown!.dropdownId, {
            displayName: name,
            description: description.trim() || null,
            options,
          })
        : await createTaskDropdown({
            dropdownId: slug,
            displayName: name,
            description: description.trim() || undefined,
            options,
          });
      onSaved(saved);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save dropdown');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit dropdown set' : 'New dropdown set'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Display Name"
              value={displayName}
              onChange={(e) => handleNameChange(e.target.value)}
              required
              fullWidth
              size="small"
              autoFocus
            />
            <TextField
              label="Dropdown ID"
              value={dropdownId}
              onChange={(e) => {
                setDropdownId(e.target.value);
                setIdManuallyEdited(true);
              }}
              required
              size="small"
              sx={{ width: { sm: 220 } }}
              slotProps={{ input: { readOnly: isEdit, sx: { fontFamily: 'monospace' } } }}
              helperText={isEdit ? 'Immutable' : 'Auto-generated from name'}
            />
          </Stack>

          <TextField
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            size="small"
          />

          <TextField
            label="Options (one per line)"
            value={optionsText}
            onChange={(e) => setOptionsText(e.target.value)}
            multiline
            minRows={5}
            fullWidth
            size="small"
            placeholder={'cannot connect to WiFi\n500Mbps+\n50Mbps+'}
          />

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={submitting}
          sx={{ color: '#aaa', '&:hover': { color: '#555', bgcolor: 'transparent' } }}
        >
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
          {submitting ? 'Saving…' : isEdit ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
